/**
 * 4D-360 viewer — phase 2c (frustum-driven LOD streaming).
 *
 * Loads the COPC hierarchy from the Worker route and walks the octree on
 * each camera change to load only nodes that are (a) inside the view
 * frustum and (b) at a LOD appropriate for the camera distance. Nodes are
 * loaded asynchronously via Range requests; not-needed nodes are evicted
 * from the scene to keep browser memory bounded.
 *
 * Architecture: see memory/project_viewer_architecture_copc.md (locked
 * 2026-04-26). This file is the phase-2c viewer; phase 1 (snap-to-point),
 * phase 3 (splat overlay), phase 4 (feature overlays) are subsequent.
 *
 * Job configurable via `?job=customer/project/job`, default test1/20-Apr-sdk10.
 */
import * as THREE from "three";
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls.js";
import { Copc, Hierarchy } from "copc";
import { createLazPerf } from "laz-perf";
// @ts-expect-error -- gaussian-splats-3d ships no TypeScript types
import * as GaussianSplats3D from "@mkkellogg/gaussian-splats-3d";

const canvas = document.getElementById("canvas") as HTMLCanvasElement;
const statusEl = document.getElementById("status") as HTMLDivElement;
const snapReadout = document.getElementById("snap-readout") as HTMLDivElement;
const snapLocked = document.getElementById("snap-locked") as HTMLDivElement;
const sliderControl = document.getElementById("slider-control") as HTMLDivElement | null;
const slider = document.getElementById("point-size") as HTMLInputElement | null;
const sliderLabel = document.getElementById("point-size-value") as HTMLSpanElement | null;

const API_BASE = "https://api.4d-360.live";
const params = new URLSearchParams(window.location.search);
const jobPath = params.get("job") ?? "test1/20-Apr-sdk10";
const layerMode = params.get("layer") ?? "points";   // "points" (COPC) | "splat" (gaussian)
const copcUrl = `${API_BASE}/api/r2/copc/${jobPath}`;
// Splat data: prefer .ksplat (compressed, ~3× faster to parse); the library
// detects format from URL extension.
const splatUrl = `${API_BASE}/api/r2/tile/${jobPath}/outputs/splat.ksplat`;
// Bbox source: still .splat (antimatter15 32-byte/gaussian format). The
// .ksplat binary layout is bucketed/compressed and not trivial to parse
// for just-the-XYZ; one extra 100KB Range fetch is cheap.
const splatBboxUrl = `${API_BASE}/api/r2/tile/${jobPath}/outputs/splat.splat`;

// LOD threshold in pixels: load a deeper octree node whenever this node's
// world-space spacing projects to more than N px on screen. Lower = sharper
// + more loaded nodes; higher = coarser + faster.
// 0.5 forces descent to the deepest available level for any in-frustum
// region — the only way to make thin features (poles/wires) look defined
// is to load every point we have for them. Source resolution is the real
// ceiling above this (depthmap_resolution=640 in stages.opensfm.run_dense).
const SSE_THRESHOLD_PX = 0.5;

// Max concurrent point-data fetches. R2 + CF Worker are happy to serve many
// in parallel; browser HTTP/2 unlocks ~100 streams per origin so 16 is fine.
const MAX_CONCURRENT_LOADS = 16;

// Debounce LOD recompute this many ms after the last camera change. Prevents
// evict-load thrashing during continuous drag while keeping post-drag
// detail-load snappy.
const LOD_DEBOUNCE_MS = 200;

// Keep loaded nodes alive for this many missed LOD passes before eviction.
// Higher = more memory but smoother panning (no reload of same node).
const EVICT_GENERATION_DELAY = 5;

// ── Renderer / scene / camera ───────────────────────────────────────────
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight, false);
renderer.setClearColor(0x0a0e14);

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(
  60,
  window.innerWidth / window.innerHeight,
  0.1,
  20000,
);
camera.position.set(0, 100, 100);

const controls = new TrackballControls(camera, canvas);
controls.rotateSpeed = 3.0;
// zoomSpeed multiplies wheel-delta. Browsers vary on delta magnitude
// (deltaMode 0 ~ pixels, can be 100+ per click on Mac); 0.4 keeps a single
// scroll click at ~10% zoom rather than blowing past the target.
controls.zoomSpeed = 0.4;
controls.panSpeed = 1.0;
controls.dynamicDampingFactor = 0.15;
controls.staticMoving = false;

window.addEventListener("resize", () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight, false);
});

// Fly-through zoom: TrackballControls' wheel-zoom is exponential — it scales
// the camera→target distance by a factor per tick, so as you approach the
// target each click moves you a tiny fraction of a metre and the zoom feels
// like it "stops". When the camera is already within FLY_THRESHOLD of the
// target, intercept the wheel and translate BOTH camera and target along the
// view direction instead. Effect: user keeps flying through the scene at
// constant speed instead of stalling at close range.
const FLY_THRESHOLD = 5.0;     // metres — below this, fly instead of zoom
const FLY_STEP = 0.5;          // metres per wheel-tick when flying
canvas.addEventListener("wheel", (ev) => {
  const dist = camera.position.distanceTo(controls.target);
  if (dist > FLY_THRESHOLD) return;   // let TrackballControls handle far zoom
  ev.preventDefault();
  ev.stopPropagation();
  // deltaY > 0 = wheel-down = zoom out, < 0 = zoom in.
  const sign = ev.deltaY < 0 ? +1 : -1;
  const dir = new THREE.Vector3();
  camera.getWorldDirection(dir);   // unit vector camera is looking along
  const step = dir.multiplyScalar(sign * FLY_STEP);
  camera.position.add(step);
  controls.target.add(step);
}, { passive: false, capture: true });

// ── Camera-state persistence ────────────────────────────────────────────
// Hard-refresh should restore the user's last view, not snap back to
// whatever the init pass framed. Per-mode key so points-mode framing
// doesn't leak to splat-mode (different coord systems / scales).
// Version suffix in the key so old/buggy saves don't override new framing.
const CAM_KEY = `viewer-camera-v3-${jobPath}-${layerMode}`;

function saveCameraState() {
  try {
    localStorage.setItem(CAM_KEY, JSON.stringify({
      pos: camera.position.toArray(),
      target: controls.target.toArray(),
      up: camera.up.toArray(),
    }));
  } catch { /* localStorage may be disabled */ }
}

function restoreCameraState(): boolean {
  try {
    const raw = localStorage.getItem(CAM_KEY);
    if (!raw) return false;
    const s = JSON.parse(raw);
    if (!s.pos || !s.target || !s.up) return false;
    // Sanity: reject saved states from buggy framing. Two patterns:
    //   (a) both pos and target at origin
    //   (b) camera within 5m of origin AND target within 5m of origin
    //       — the radius-1-quad-bbox framing bug at (0, 0.3, 1.0)→(0,0,0)
    // Real splat-mode framings put the camera tens-to-hundreds of metres
    // off origin (data lives at Y~170m for our Texas test).
    const posLen = Math.hypot(s.pos[0], s.pos[1], s.pos[2]);
    const tgtLen = Math.hypot(s.target[0], s.target[1], s.target[2]);
    if (posLen < 5 && tgtLen < 5) {
      console.warn(`[viewer] discarding degenerate saved camera state ` +
                   `(pos|=${posLen.toFixed(2)}, tgt|=${tgtLen.toFixed(2)})`);
      localStorage.removeItem(CAM_KEY);
      return false;
    }
    camera.position.fromArray(s.pos);
    controls.target.fromArray(s.target);
    camera.up.fromArray(s.up);
    camera.lookAt(controls.target);
    controls.update();
    return true;
  } catch {
    return false;
  }
}

let saveTimer: number | null = null;
controls.addEventListener("change", () => {
  if (saveTimer !== null) clearTimeout(saveTimer);
  saveTimer = window.setTimeout(saveCameraState, 500);
});

// ── COPC streaming state ────────────────────────────────────────────────
type NodeState = {
  key: string;            // "depth-x-y-z"
  depth: number;
  bbox: THREE.Box3;       // bounding box in scene-space (after offset)
  spacing: number;        // node's effective point spacing in metres
  hierarchyNode: Hierarchy.Node;
  points?: THREE.Points;  // null if not loaded
  loading?: boolean;
  generation: number;     // last frame this was selected — for eviction
};

let copc: Copc;
let lazPerf: any;
let dataOffset = new THREE.Vector3();   // applied to every loaded point so scene origin sits at data center
const allNodes = new Map<string, NodeState>();
const knownPages = new Set<string>();
let pendingPages: { key: string; page: Hierarchy.Page }[] = [];
const sharedMaterial = new THREE.PointsMaterial({
  size: 3,
  sizeAttenuation: false,
  vertexColors: true,
});
let frameGen = 0;       // bumped on each LOD recompute; nodes whose generation lags get evicted

function nodeBbox(key: string, cube: number[]): THREE.Box3 {
  const [d, x, y, z] = key.split("-").map(Number);
  const sideRoot = cube[3] - cube[0];
  const side = sideRoot / Math.pow(2, d);
  const ox = cube[0] + x * side;
  const oy = cube[1] + y * side;
  const oz = cube[2] + z * side;
  return new THREE.Box3(
    new THREE.Vector3(ox, oy, oz).sub(dataOffset),
    new THREE.Vector3(ox + side, oy + side, oz + side).sub(dataOffset),
  );
}

function childKeys(key: string): string[] {
  const [d, x, y, z] = key.split("-").map(Number);
  const cd = d + 1;
  const out: string[] = [];
  for (let dx = 0; dx < 2; dx++)
    for (let dy = 0; dy < 2; dy++)
      for (let dz = 0; dz < 2; dz++)
        out.push(`${cd}-${x * 2 + dx}-${y * 2 + dy}-${z * 2 + dz}`);
  return out;
}

async function ingestPage(pageKey: string, page: Hierarchy.Page) {
  if (knownPages.has(pageKey)) return;
  knownPages.add(pageKey);
  const subtree = await Copc.loadHierarchyPage(copcUrl, page);
  for (const [k, hn] of Object.entries(subtree.nodes)) {
    if (hn && !allNodes.has(k)) {
      const depth = parseInt(k.split("-")[0], 10);
      allNodes.set(k, {
        key: k,
        depth,
        bbox: nodeBbox(k, copc.info.cube),
        spacing: copc.info.spacing / Math.pow(2, depth),
        hierarchyNode: hn,
        generation: 0,
      });
    }
  }
  for (const [k, p] of Object.entries(subtree.pages)) {
    if (p) pendingPages.push({ key: k, page: p });
  }
}

// ── LOD selection: walk octree depth-first, pick shallowest node whose
//    screen-space spacing falls below SSE_THRESHOLD_PX.
const _frustum = new THREE.Frustum();
const _projScreen = new THREE.Matrix4();
const _tmpCenter = new THREE.Vector3();

function pixelSpacing(node: NodeState, dist: number): number {
  // Spacing in pixels at the camera: world_spacing × (canvas_height/2) / (dist × tan(fov_y/2))
  const halfHeight = renderer.domElement.height / 2;
  const halfFov = THREE.MathUtils.degToRad(camera.fov / 2);
  return (node.spacing / dist) * (halfHeight / Math.tan(halfFov));
}

function selectLOD(): NodeState[] {
  _projScreen.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  _frustum.setFromProjectionMatrix(_projScreen);
  const out: NodeState[] = [];

  // Lazy hierarchy expansion: while we have pending pages near the root, kick
  // them off — we want full top-of-tree before culling. Deeper pages load on demand.
  while (pendingPages.length > 0) {
    const p = pendingPages.shift()!;
    // Fire-and-forget; result populates allNodes for next selectLOD call.
    ingestPage(p.key, p.page).catch((e) => console.warn("page ingest failed", p.key, e));
  }

  function visit(key: string) {
    const n = allNodes.get(key);
    if (!n) return;
    if (!_frustum.intersectsBox(n.bbox)) return;
    n.bbox.getCenter(_tmpCenter);
    const dist = Math.max(camera.position.distanceTo(_tmpCenter), 0.01);
    const pxSp = pixelSpacing(n, dist);
    if (pxSp < SSE_THRESHOLD_PX) {
      out.push(n);
      return;
    }
    // descend
    const cks = childKeys(key);
    let descended = false;
    for (const ck of cks) {
      if (allNodes.has(ck)) {
        descended = true;
        visit(ck);
      }
    }
    if (!descended) {
      // leaf (or children not yet hierarchy-loaded) — use this LOD
      out.push(n);
    }
  }

  visit("0-0-0-0");
  return out;
}

// ── Async load queue ────────────────────────────────────────────────────
const loadQueue: NodeState[] = [];
let loadingNow = 0;

function enqueueLoad(node: NodeState) {
  if (node.points || node.loading) return;
  if (loadQueue.includes(node)) return;
  node.loading = true;
  loadQueue.push(node);
  pumpLoadQueue();
}

function pumpLoadQueue() {
  while (loadingNow < MAX_CONCURRENT_LOADS && loadQueue.length > 0) {
    const node = loadQueue.shift()!;
    loadingNow++;
    loadNode(node).finally(() => {
      loadingNow--;
      pumpLoadQueue();
    });
  }
}

async function loadNode(node: NodeState) {
  try {
    const view = await Copc.loadPointDataView(copcUrl, copc, node.hierarchyNode, { lazPerf });
    const n = view.pointCount;
    if (n === 0) {
      node.loading = false;
      return;
    }
    const getX = view.getter("X");
    const getY = view.getter("Y");
    const getZ = view.getter("Z");
    const hasColor = "Red" in view.dimensions;
    const getR = hasColor ? view.getter("Red") : null;
    const getG = hasColor ? view.getter("Green") : null;
    const getB = hasColor ? view.getter("Blue") : null;

    const positions = new Float32Array(n * 3);
    const colors = new Float32Array(n * 3);
    let maxRgb = 0;
    for (let i = 0; i < n; i++) {
      positions[i * 3] = getX(i) - dataOffset.x;
      positions[i * 3 + 1] = getY(i) - dataOffset.y;
      positions[i * 3 + 2] = getZ(i) - dataOffset.z;
      if (getR && getG && getB) {
        const r = getR(i), g = getG(i), b = getB(i);
        colors[i * 3] = r; colors[i * 3 + 1] = g; colors[i * 3 + 2] = b;
        if (r > maxRgb) maxRgb = r;
        if (g > maxRgb) maxRgb = g;
        if (b > maxRgb) maxRgb = b;
      } else {
        colors[i * 3] = colors[i * 3 + 1] = colors[i * 3 + 2] = 0.5;
      }
    }
    if (hasColor) {
      const divisor = maxRgb > 255 ? 65535 : 255;
      // Normalise + sRGB→linear for Three.js's SRGBColorSpace output.
      for (let i = 0; i < n * 3; i++) {
        const c = colors[i] / divisor;
        colors[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    geometry.computeBoundingSphere();
    const points = new THREE.Points(geometry, sharedMaterial);
    points.frustumCulled = true;
    node.points = points;
    scene.add(points);
  } catch (e) {
    console.warn(`[copc] failed loading ${node.key}:`, e);
  } finally {
    node.loading = false;
  }
}

function evictUnusedNodes() {
  for (const node of allNodes.values()) {
    if (node.points && node.generation < frameGen - EVICT_GENERATION_DELAY) {
      scene.remove(node.points);
      node.points.geometry.dispose();
      // sharedMaterial is shared across nodes — don't dispose
      node.points = undefined;
    }
  }
}

// ── Camera-debounced LOD update ─────────────────────────────────────────
// setTimeout-based: only fires LOD_DEBOUNCE_MS after the LAST camera change.
// During continuous drag we accumulate many "change" events but only one LOD
// pass runs once the user releases — avoids evict/reload thrash.
let pendingUpdate: number | null = null;
function scheduleLODUpdate() {
  if (pendingUpdate !== null) clearTimeout(pendingUpdate);
  pendingUpdate = window.setTimeout(() => {
    pendingUpdate = null;
    runLODUpdate();
  }, LOD_DEBOUNCE_MS);
}

function runLODUpdate() {
  frameGen++;
  const wanted = selectLOD();
  for (const n of wanted) {
    n.generation = frameGen;
    if (!n.points && !n.loading) enqueueLoad(n);
  }
  evictUnusedNodes();
  updateHud(wanted);
}

function updateHud(wanted: NodeState[]) {
  let loaded = 0, totalPts = 0;
  for (const n of allNodes.values()) {
    if (n.points) {
      loaded++;
      totalPts += (n.points.geometry.attributes.position as THREE.BufferAttribute).count;
    }
  }
  statusEl.textContent =
    `${wanted.length} nodes selected · ${loaded}/${allNodes.size} loaded · ` +
    `${totalPts.toLocaleString()} points · ${loadQueue.length + loadingNow} in queue`;
}

// ── Bootstrap ───────────────────────────────────────────────────────────
async function init() {
  statusEl.textContent = `opening ${copcUrl}…`;
  copc = await Copc.create(copcUrl);
  lazPerf = await createLazPerf({ locateFile: () => "/laz-perf.wasm" });

  // Centre on the data extent (LAS header min/max) so float32 precision
  // stays high near the scene origin.
  const min = copc.header.min;
  const max = copc.header.max;
  dataOffset.set(
    (min[0] + max[0]) / 2,
    (min[1] + max[1]) / 2,
    (min[2] + max[2]) / 2,
  );
  console.log("data extent:", { min, max, offset: dataOffset.toArray() });

  // Initial: ingest the root hierarchy page, then everything pendingPages
  // refers to (load eagerly so we have the full tree for culling).
  await ingestPage("root", copc.info.rootHierarchyPage);
  while (pendingPages.length > 0) {
    const batch = pendingPages.splice(0, pendingPages.length);
    await Promise.all(batch.map((p) => ingestPage(p.key, p.page)));
  }
  console.log(`hierarchy loaded: ${allNodes.size} nodes`);

  // Frame the camera on the data extent
  const extent = new THREE.Vector3().subVectors(
    new THREE.Vector3(...max),
    new THREE.Vector3(...min),
  );
  const r = Math.max(extent.x, extent.y, extent.z) / 2;
  camera.position.set(0, r * 0.6, r * 1.0);
  camera.lookAt(0, 0, 0);
  controls.target.set(0, 0, 0);
  controls.update();

  // Wire size slider
  if (slider && sliderLabel && sliderControl) {
    slider.value = String(sharedMaterial.size);
    sliderLabel.textContent = String(sharedMaterial.size);
    sliderControl.style.display = "block";
    slider.style.display = "inline-block";
    slider.addEventListener("input", () => {
      const v = Number(slider.value);
      sharedMaterial.size = v;
      sliderLabel.textContent = String(v);
    });
  }

  // Restore saved camera state if present (overrides the init framing).
  // Done AFTER init's framing call so a missing save doesn't crash;
  // done BEFORE the first LOD pass so we load nodes for the user's
  // actual viewport, not the default framing's.
  if (restoreCameraState()) {
    console.log("[viewer] restored camera from localStorage");
  }

  // First LOD pass + hook camera changes to subsequent passes.
  controls.addEventListener("change", scheduleLODUpdate);
  scheduleLODUpdate();

  // Pole tubes overlay — best-effort; doesn't block init if missing.
  loadPolesOverlay().catch((e) =>
    console.warn("[viewer] poles overlay skipped:", e),
  );
}

// ── Pole tubes overlay ──────────────────────────────────────────────────
// Fetch poles_3d.json (emitted by stages.poles_geometric) and render each
// candidate as a vertical cylinder. Accepted poles (is_pole=true, passed
// crossarm gate) are green; rejected candidates are red. Both are visible
// so we can compare against the dense cloud's actual pole geometry.
async function loadPolesOverlay() {
  const url = `${API_BASE}/api/r2/tile/${jobPath}/outputs/poles_3d.json`;
  const resp = await fetch(url);
  if (!resp.ok) {
    console.warn(`[viewer] poles_3d.json: HTTP ${resp.status}`);
    return;
  }
  const data = await resp.json();
  const poles = data.poles ?? [];
  console.log(`[viewer] loading ${poles.length} pole tubes`);

  // If init() didn't run (no COPC), dataOffset is still (0,0,0). Compute
  // it from poles' centroid so cylinders render near scene origin and
  // float32 precision stays high.
  if (poles.length > 0 && dataOffset.lengthSq() === 0) {
    let sx = 0, sy = 0, sz = 0;
    for (const p of poles) {
      sx += p.x; sy += p.y; sz += (p.z_base + p.z_top) / 2;
    }
    dataOffset.set(sx / poles.length, sy / poles.length, sz / poles.length);
    console.log("[viewer] dataOffset from poles:", dataOffset.toArray());
  }

  const TUBE_RADIUS = 0.25;        // m — visible at typical viewing distance
  const RADIAL_SEG = 12;
  const acceptedMat = new THREE.MeshBasicMaterial({
    color: 0x33dd66, transparent: true, opacity: 0.7,
  });
  const rejectedMat = new THREE.MeshBasicMaterial({
    color: 0xdd3333, transparent: true, opacity: 0.55,
  });

  for (const p of poles) {
    const h = Math.max(0.5, p.z_top - p.z_base);
    const geom = new THREE.CylinderGeometry(
      TUBE_RADIUS, TUBE_RADIUS, h, RADIAL_SEG,
    );
    // CylinderGeometry's axis is local +Y. Rotate so axis becomes +Z (our
    // up direction in ENU). That's a 90° rotation about the +X axis.
    geom.rotateX(Math.PI / 2);

    const mesh = new THREE.Mesh(
      geom, p.is_pole ? acceptedMat : rejectedMat,
    );
    // Position centre of cylinder at midpoint between z_base and z_top.
    // dataOffset is in the same ENU frame as the pole's (x, y, z).
    mesh.position.set(
      p.x - dataOffset.x,
      p.y - dataOffset.y,
      (p.z_base + p.z_top) / 2 - dataOffset.z,
    );
    mesh.userData.poleInfo = p;     // for hover/click lookup later
    scene.add(mesh);
  }
  console.log(`[viewer] poles overlay added`);
}

// Splat mode: gaussian splat overlay, replaces COPC streaming.
// Snap-to-point + box-zoom are points-mode-only (raycast against Three.js
// Points objects); splat mode disables them.
let splatDropIn: any = null;     // kept for compat; standalone Viewer doesn't use it

async function initSplat() {
  statusEl.textContent = `opening splat ${splatUrl}… (standalone Viewer)`;
  // PIVOT 2026-04-27: switching from DropInViewer to standalone Viewer
  // with its own canvas. Multiple iterations of DropInViewer-into-our-
  // scene approach left renderer.info stuck at 1 draw call (callback
  // mesh) — Three.js consistently refused to issue a draw call for the
  // splat-quad mesh despite all properties (visible, frustumCulled,
  // layers, material, position) being correct. Standalone Viewer
  // sidesteps the integration entirely; if THIS doesn't render, the
  // issue is library/data/browser, not our scene.
  //
  // Side effects:
  //   - Our canvas hidden (display: none) so it doesn't visually conflict
  //   - Library creates its own canvas inside #splat-root
  //   - Snap-to-point + box-zoom unavailable in splat mode (no shared scene)
  //   - Our render loop continues but draws to a hidden canvas; harmless
  canvas.style.display = "none";
  const splatRoot = document.getElementById("splat-root") as HTMLDivElement | null
    ?? (() => {
      const d = document.createElement("div");
      d.id = "splat-root";
      d.style.cssText = "position:fixed; inset:0; z-index:0";
      document.body.insertBefore(d, document.body.firstChild);
      return d;
    })();

  const viewer = new GaussianSplats3D.Viewer({
    rootElement: splatRoot,
    selfDrivenMode: true,
    useBuiltInControls: true,
    cameraUp: [0, 1, 0],
    initialCameraPosition: [0, 250, 150],
    initialCameraLookAt: [0, 165, 0],
    sharedMemoryForWorkers: false,
    // Try CPU sort path — gpuAcceleratedSort=true relies on a compute-shader
    // sort that may fail silently without the right WebGL2 extensions, leaving
    // instance count at 0 and producing 0 draw calls.
    gpuAcceleratedSort: false,
  });
  await viewer.init();
  await viewer.addSplatScene(splatUrl, {
    showLoadingUI: true,
    streamView: true,                 // (D) progressive: render gaussians as they arrive
    progressiveLoad: true,
    onProgress: (pct: number, msg: string, stage: string) => {
      console.log(`[splat] progress ${pct.toFixed(1)}% [${stage}] ${msg}`);
      statusEl.textContent = `splat ${stage}: ${pct.toFixed(0)}%${msg ? " — " + msg : ""}`;
    },
  });
  viewer.start();
  statusEl.textContent = `splat layer loaded · use mouse to orbit`;
  console.log("[splat] standalone Viewer started");
  // Diagnose what the library actually put in splat-root.
  console.log("[splat] splat-root children:", splatRoot.children.length);
  splatRoot.querySelectorAll("*").forEach((el) => {
    const rect = (el as HTMLElement).getBoundingClientRect();
    console.log("[splat-dom]", el.tagName, "id=", el.id, "class=", el.className,
                "size=", rect.width, "x", rect.height,
                "at=", rect.left, rect.top,
                "visible=", (el as HTMLElement).offsetParent !== null,
                "display=", getComputedStyle(el as HTMLElement).display,
                "z-index=", getComputedStyle(el as HTMLElement).zIndex);
  });
  // Force the library's canvas to fill the viewport in case it created a
  // default-size 300x150 one.
  const libCanvas = splatRoot.querySelector("canvas") as HTMLCanvasElement | null;
  if (libCanvas) {
    libCanvas.style.cssText = "position:absolute; inset:0; width:100%; height:100%;";
    libCanvas.width = window.innerWidth;
    libCanvas.height = window.innerHeight;
    console.log("[splat] forced library canvas to", libCanvas.width, "x", libCanvas.height);
  } else {
    console.warn("[splat] no <canvas> inside splat-root!");
  }
  // Hide the loading-UI overlays that the lib leaves at z-index >= 1 covering
  // the canvas. (spinnerOuterContainer0 was 1363x924 visible=true after load.)
  splatRoot.querySelectorAll(
    ".spinnerOuterContainer0, .spinnerContainerMin0, .progressBarOuterContainer, .progressBarBox"
  ).forEach((el) => ((el as HTMLElement).style.display = "none"));
  console.log("[splat] hid loading-UI overlays");
  // Peek at the library's own renderer so we know if IT is issuing draws.
  setInterval(() => {
    const libRenderer = (viewer as any).renderer;
    if (libRenderer?.info) {
      const i = libRenderer.info;
      console.log(`[splat-lib-renderer] ${i.render.calls} calls / ` +
                  `${i.render.triangles} tris, ${i.memory.geometries} geos, ` +
                  `${i.memory.textures} tex, frame=${i.render.frame}`);
    } else {
      console.warn("[splat-lib-renderer] viewer has no .renderer property");
    }
  }, 5000);
  return;     // skip the legacy DropInViewer code path below
  // Legacy DropInViewer code preserved temporarily — unreachable; will
  // be deleted once standalone Viewer confirmed working end-to-end.
  const dropIn = new GaussianSplats3D.DropInViewer({
    selfDrivenMode: false,           // we drive update() from our render loop
    sharedMemoryForWorkers: false,   // CF Pages doesn't ship COOP/COEP headers
    gpuAcceleratedSort: true,
  });
  splatDropIn = dropIn;
  scene.add(dropIn);
  // Bare-default options + explicit progress logging so we can see how
  // far through the parse we get. Earlier `streamView: true,
  // progressiveLoad: true` produced only the callbackMesh in renderer.info
  // — looked like the splat shader pipeline never set up draw calls.
  await dropIn.addSplatScene(splatUrl, {
    showLoadingUI: true,
    onProgress: (pct: number, msg: string, stage: string) => {
      console.log(`[splat] progress ${pct.toFixed(1)}% [${stage}] ${msg}`);
      statusEl.textContent = `splat ${stage}: ${pct.toFixed(0)}%${msg ? " — " + msg : ""}`;
    },
  });
  console.log("[splat] addSplatScene resolved; viewer.splatMesh:",
              !!(dropIn as any).viewer?.splatMesh,
              "splatRenderReady:", !!(dropIn as any).viewer?.splatRenderReady);

  // CRITICAL FIX: the splat mesh's geometry.boundingBox is the RENDER
  // QUAD bbox (radius ~1 at origin), not the data extent. Three.js's
  // default frustumCulled=true then culls the mesh out of view because
  // its tiny origin-bbox doesn't intersect a camera frustum positioned
  // at (-11, 203, 185) looking at the actual data at Y~170. Result:
  // splat geometry never drawn, only the library's tiny callbackMesh
  // (which is correctly frustumCulled=false) shows in renderer.info.
  // Walking the tree to disable culling everywhere in the splat subtree.
  let nDisabled = 0;
  dropIn.traverse((obj: THREE.Object3D) => {
    if (obj.frustumCulled) {
      obj.frustumCulled = false;
      nDisabled++;
    }
  });
  console.log(`[splat] disabled frustumCulled on ${nDisabled} splat nodes`);

  // Deeper diagnostic: for each Mesh in the splat subtree, dump every
  // property Three.js's renderer uses to decide whether to issue a
  // draw call. We're seeing only callbackMesh draw despite splat data
  // being in GPU (2 geos, 4 tex). Want to find which property is
  // wrong on the splat-data mesh.
  dropIn.traverse((obj: THREE.Object3D) => {
    const m = obj as THREE.Mesh;
    if (!m.isMesh) return;
    const g = m.geometry;
    const mat: any = m.material;
    console.log("[splat-debug]",
      "type=", obj.type, "name=", obj.name || "(unnamed)",
      "\n  visible=", m.visible,
      "frustumCulled=", m.frustumCulled,
      "renderOrder=", m.renderOrder,
      "layers.mask=", m.layers.mask,
      "\n  matVisible=", mat?.visible,
      "transparent=", mat?.transparent,
      "matType=", mat?.type || mat?.constructor?.name,
      "\n  geomType=", g?.constructor?.name,
      "posCount=", (g?.getAttribute?.("position") as THREE.BufferAttribute | undefined)?.count,
      "drawRange=", g?.drawRange,
      "boundingSphere=", g?.boundingSphere,
      "\n  position=", obj.position.toArray(),
      "scale=", obj.scale.toArray(),
    );
  });
  console.log("[splat-debug] camera.layers.mask=", camera.layers.mask);
  statusEl.textContent = `splat layer loaded · use mouse to orbit`;
  // Splat data is in BAKED Y-up ENU coords (per stage_gaussian_splat /
  // _rotate_enu_to_yup): X=east, Y=altitude (was ENU Z), Z=-north.
  // For test1 over Dallas road corridor, camera altitude is ~+170 m
  // and corridor Y-axis (now splat -Z) spans ~−270..+245 m. Frame the
  // camera up-and-behind the centre of that corridor.
  //
  // Use the loaded splat's actual bounding box if the library exposes
  // it; fall back to test1-tuned defaults.
  const splatBox = new THREE.Box3();
  const tmpVec = new THREE.Vector3();
  let foundBox = false;
  dropIn.traverse((obj: THREE.Object3D) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.geometry && mesh.geometry.boundingBox) {
      const b = mesh.geometry.boundingBox.clone();
      mesh.updateWorldMatrix(true, false);
      b.applyMatrix4(mesh.matrixWorld);
      if (foundBox) splatBox.union(b);
      else { splatBox.copy(b); foundBox = true; }
    }
  });
  // The library's geometry boundingBox is the RENDER QUAD bbox (radius
  // ~1 around origin); it doesn't reflect actual gaussian positions
  // because those live in instanced buffers / shader uniforms, not in
  // the standard `position` attribute. To frame on the real data, fetch
  // the first 100 KB of the .splat file via HTTP Range and parse the
  // antimatter15 format directly: 32 bytes per gaussian, first 12 bytes
  // are XYZ float32×3 in little-endian.
  try {
    const resp = await fetch(splatBboxUrl, {
      headers: { Range: "bytes=0-102399" },
    });
    if (resp.ok) {
      const buf = await resp.arrayBuffer();
      const dv = new DataView(buf);
      const n = Math.floor(buf.byteLength / 32);
      let minX = Infinity, minY = Infinity, minZ = Infinity;
      let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
      for (let i = 0; i < n; i++) {
        const x = dv.getFloat32(i * 32 + 0, true);
        const y = dv.getFloat32(i * 32 + 4, true);
        const z = dv.getFloat32(i * 32 + 8, true);
        if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
          if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z;
          if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z;
        }
      }
      if (n > 10 && Number.isFinite(maxX - minX)) {
        splatBox.set(
          new THREE.Vector3(minX, minY, minZ),
          new THREE.Vector3(maxX, maxY, maxZ),
        );
        foundBox = true;
        console.log(`[splat] sampled ${n} gaussians from first 100 KB; bbox`,
                    [minX, minY, minZ, maxX, maxY, maxZ]);
      }
    }
  } catch (e) {
    console.warn("[splat] sample-bbox fetch failed:", e);
  }

  if (foundBox) {
    splatBox.getCenter(tmpVec);
    const size = splatBox.getSize(new THREE.Vector3());
    const r = Math.max(size.x, size.y, size.z) / 2;
    console.log("[splat] sample bbox centre", tmpVec.toArray(), "radius", r);
    controls.target.copy(tmpVec);
    camera.position.set(tmpVec.x, tmpVec.y + r * 0.4, tmpVec.z + r * 1.2);
  } else {
    console.warn("[splat] sample-bbox unavailable — test1-tuned default");
    controls.target.set(0, 165, 0);
    camera.position.set(0, 250, 150);
  }
  camera.lookAt(controls.target);
  controls.update();
  // Restore saved camera state if present (overrides default framing
  // so hard-refresh preserves the user's view).
  if (restoreCameraState()) {
    console.log("[viewer] restored camera from localStorage");
  }
}

// HUD: reset-view link clears localStorage + reloads
const resetLink = document.getElementById("reset-view");
if (resetLink) {
  resetLink.addEventListener("click", (e) => {
    e.preventDefault();
    localStorage.removeItem(CAM_KEY);
    location.reload();
  });
}

// HUD: highlight current layer mode + reflect in the title
const modeLabel = document.getElementById("mode-label");
if (modeLabel) {
  modeLabel.textContent = layerMode === "splat"
    ? "phase-3 (splat layer)"
    : "phase-2c (LOD streaming)";
}
const activeLink = document.getElementById(`layer-${layerMode}`);
if (activeLink) (activeLink as HTMLElement).style.fontWeight = "700";

const startup = layerMode === "splat" ? initSplat() : init();
startup.catch((err) => {
  console.error(err);
  // Soft-fail when the dense COPC isn't built yet for this job. Still try
  // to render the pole-tubes overlay so the user can see those even with
  // no point-cloud backdrop — picks a sensible default offset from the
  // poles' own centroid.
  if (layerMode !== "splat") {
    statusEl.textContent =
      `dense COPC missing (${err.message ?? err}); rendering pole tubes only`;
    loadPolesOverlay()
      .then(() => framePolesOnly())
      .catch((e) =>
        console.warn("[viewer] poles overlay also failed:", e),
      );
  } else {
    statusEl.textContent = `failed: ${err.message ?? err}`;
  }
});

// When COPC fetch fails we fall back to framing on the poles themselves.
// Walks the scene for the cylinder meshes added by loadPolesOverlay() and
// fits the camera to their bounding box.
function framePolesOnly() {
  const bb = new THREE.Box3();
  let any = false;
  scene.traverse((obj) => {
    if ((obj as any).userData?.poleInfo) {
      bb.expandByObject(obj);
      any = true;
    }
  });
  if (!any) return;
  const ctr = new THREE.Vector3();
  bb.getCenter(ctr);
  const sz = new THREE.Vector3();
  bb.getSize(sz);
  const r = Math.max(sz.x, sz.y, sz.z) || 50;
  camera.position.set(ctr.x + r, ctr.y + r, ctr.z + r);
  camera.lookAt(ctr);
  controls.target.copy(ctr);
  controls.update();
}

// ── Snap-to-point (phase 1) ─────────────────────────────────────────────
// Raycast cursor against currently-visible Points objects. Three.js
// Raycaster handles the per-Points iteration; precision is set by
// params.Points.threshold (world-space radius). At denser LODs we tighten
// it; at coarse LODs we widen so single-pixel-distant clicks still hit.
const raycaster = new THREE.Raycaster();
raycaster.params.Points = { threshold: 0.3 };  // metres; auto-tuned in mousemove
const ndcMouse = new THREE.Vector2();

const snapMarker = new THREE.Mesh(
  new THREE.SphereGeometry(0.4, 16, 12),
  new THREE.MeshBasicMaterial({ color: 0xff00ff, depthTest: false }),
);
snapMarker.renderOrder = 999;     // always render on top of points
snapMarker.visible = false;
scene.add(snapMarker);

const lockMarker = new THREE.Mesh(
  new THREE.SphereGeometry(0.5, 16, 12),
  new THREE.MeshBasicMaterial({ color: 0xf59e0b, depthTest: false, wireframe: true }),
);
lockMarker.renderOrder = 998;
lockMarker.visible = false;
scene.add(lockMarker);

function visiblePointsTargets(): THREE.Object3D[] {
  const out: THREE.Object3D[] = [];
  for (const node of allNodes.values()) {
    if (node.points) out.push(node.points);
  }
  return out;
}

function ndcFromEvent(e: PointerEvent) {
  const rect = canvas.getBoundingClientRect();
  ndcMouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  ndcMouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
}

function snapAtCursor(): { local: THREE.Vector3; enu: THREE.Vector3 } | null {
  raycaster.setFromCamera(ndcMouse, camera);
  // Adapt threshold to camera distance + current point spacing so close-up
  // clicks are precise, far-away clicks still register. Heuristic: ~5x
  // current displayed point pixel-size in world units.
  const targetDist = camera.position.distanceTo(controls.target);
  const halfFov = THREE.MathUtils.degToRad(camera.fov / 2);
  const worldPerPixel = (targetDist * Math.tan(halfFov)) / (renderer.domElement.height / 2);
  raycaster.params.Points.threshold = Math.max(worldPerPixel * sharedMaterial.size * 1.5, 0.05);

  const hits = raycaster.intersectObjects(visiblePointsTargets(), false);
  if (hits.length === 0) return null;
  const local = hits[0].point.clone();
  const enu = local.clone().add(dataOffset);
  return { local, enu };
}

canvas.addEventListener("pointermove", (e) => {
  ndcFromEvent(e);
  const snap = snapAtCursor();
  if (snap) {
    snapReadout.textContent =
      `snap: E=${snap.enu.x.toFixed(3)}  N=${snap.enu.y.toFixed(3)}  U=${snap.enu.z.toFixed(3)} m`;
    snapMarker.position.copy(snap.local);
    snapMarker.visible = true;
  } else {
    snapReadout.textContent = "snap: hover over points";
    snapMarker.visible = false;
  }
});

canvas.addEventListener("click", (e) => {
  ndcFromEvent(e as unknown as PointerEvent);
  const snap = snapAtCursor();
  if (!snap) {
    snapLocked.textContent = "";
    lockMarker.visible = false;
    return;
  }
  snapLocked.textContent =
    `locked: E=${snap.enu.x.toFixed(3)}  N=${snap.enu.y.toFixed(3)}  U=${snap.enu.z.toFixed(3)} m`;
  lockMarker.position.copy(snap.local);
  lockMarker.visible = true;
});

// ── Box-zoom (shift+drag) ───────────────────────────────────────────────
// On shift+left-drag: draw a 2D rectangle, then on release project every
// currently-loaded point to screen space, find points inside the rect,
// compute their world-space AABB, frame the camera on it. This is what
// lets Walter say "zoom in on this pole" by drawing a box around it.
const boxOverlay = document.createElement("div");
Object.assign(boxOverlay.style, {
  position: "fixed",
  border: "1.5px dashed #22d3ee",
  background: "rgba(34,211,238,0.08)",
  pointerEvents: "none",
  display: "none",
  zIndex: "10",
});
document.body.appendChild(boxOverlay);

let boxStart: { x: number; y: number } | null = null;

canvas.addEventListener("pointerdown", (e) => {
  if (e.shiftKey && e.button === 0) {
    e.preventDefault();
    e.stopPropagation();
    controls.enabled = false;
    boxStart = { x: e.clientX, y: e.clientY };
    Object.assign(boxOverlay.style, {
      display: "block",
      left: `${e.clientX}px`,
      top: `${e.clientY}px`,
      width: "0px",
      height: "0px",
    });
  }
}, true);

window.addEventListener("pointermove", (e) => {
  if (!boxStart) return;
  const x1 = Math.min(boxStart.x, e.clientX);
  const y1 = Math.min(boxStart.y, e.clientY);
  const w = Math.abs(e.clientX - boxStart.x);
  const h = Math.abs(e.clientY - boxStart.y);
  Object.assign(boxOverlay.style, {
    left: `${x1}px`, top: `${y1}px`, width: `${w}px`, height: `${h}px`,
  });
});

window.addEventListener("pointerup", (e) => {
  if (!boxStart) return;
  const rect = canvas.getBoundingClientRect();
  const x1 = Math.min(boxStart.x, e.clientX) - rect.left;
  const y1 = Math.min(boxStart.y, e.clientY) - rect.top;
  const x2 = Math.max(boxStart.x, e.clientX) - rect.left;
  const y2 = Math.max(boxStart.y, e.clientY) - rect.top;
  boxStart = null;
  boxOverlay.style.display = "none";
  controls.enabled = true;
  if (x2 - x1 < 5 || y2 - y1 < 5) return;   // tiny rect = treat as click

  // Project all visible points to screen, AABB the ones inside rect.
  const proj = new THREE.Vector3();
  const min = new THREE.Vector3(Infinity, Infinity, Infinity);
  const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
  let hits = 0;
  const W = rect.width, H = rect.height;

  for (const node of allNodes.values()) {
    if (!node.points) continue;
    if (!node.points.visible) continue;
    const positions = node.points.geometry.getAttribute("position") as THREE.BufferAttribute;
    const arr = positions.array as Float32Array;
    const count = positions.count;
    for (let i = 0; i < count; i++) {
      const px = arr[i * 3], py = arr[i * 3 + 1], pz = arr[i * 3 + 2];
      proj.set(px, py, pz).project(camera);
      if (proj.z < -1 || proj.z > 1) continue;     // outside near/far
      const sx = ((proj.x + 1) / 2) * W;
      const sy = ((1 - proj.y) / 2) * H;
      if (sx < x1 || sx > x2 || sy < y1 || sy > y2) continue;
      hits++;
      if (px < min.x) min.x = px;
      if (py < min.y) min.y = py;
      if (pz < min.z) min.z = pz;
      if (px > max.x) max.x = px;
      if (py > max.y) max.y = py;
      if (pz > max.z) max.z = pz;
    }
  }
  if (hits < 5) {
    console.log("box-zoom: too few points inside rect", hits);
    return;
  }

  // Frame camera on AABB. Maintain current view direction; recompute
  // distance so the AABB fills ~70% of viewport.
  const center = new THREE.Vector3().addVectors(min, max).multiplyScalar(0.5);
  const size = new THREE.Vector3().subVectors(max, min);
  const radius = Math.max(size.x, size.y, size.z) / 2;
  const halfFov = THREE.MathUtils.degToRad(camera.fov / 2);
  const distance = (radius / Math.tan(halfFov)) * 1.4;     // 1.4x = ~70% fill

  const dir = camera.position.clone().sub(controls.target).normalize();
  controls.target.copy(center);
  camera.position.copy(center).addScaledVector(dir, distance);
  controls.update();
  scheduleLODUpdate();   // pull deeper LOD for the new view
  console.log("box-zoom:", hits, "points framed; AABB radius", radius.toFixed(2), "m");
});

// ── Render loop ─────────────────────────────────────────────────────────
let frameCount = 0;
function animate() {
  requestAnimationFrame(animate);
  controls.update();
  // NOTE: deliberately NOT calling splatDropIn.viewer.update() here.
  // DropInViewer attaches an onBeforeRender handler to a hidden callback
  // mesh that fires during scene traversal — that's the canonical hook.
  // Calling update() externally on top of that double-fires the GPU
  // sort + uniform-set passes, which leaves the shader's per-frame
  // state inconsistent (verified 2026-04-27: renderer.info stayed at
  // 1 draw-call/frame despite splat data being correctly uploaded).
  renderer.render(scene, camera);
  // Debug: dump renderer stats every ~5 s so we can tell if anything is
  // actually being drawn (gaussian-splats-3d uses many small draw calls).
  if (++frameCount % 300 === 0 && layerMode === "splat") {
    const i = renderer.info;
    console.log(`[splat] renderer: ${i.render.calls} calls / ` +
                `${i.render.triangles} tris / ${i.render.points} points / ` +
                `${i.render.lines} lines, ${i.memory.geometries} geos, ` +
                `${i.memory.textures} tex`);
  }
}
animate();
